import React from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Box } from "@mui/material";

import PreviewFormDialog from "./components/preview/PreviewFormDialog";
import PDFEditorHeader from "./components/PDFEditorHeader";
import PDFEditorControlsBar from "./components/PDFEditorControlsBar";
import PDFEditorLeftPanel from "./components/PDFEditorLeftPanel";
import ObjectInspectorDrawer from "./components/ObjectInspectorDrawer";
import PDFCanvas from "./components/PDFCanvas";
import { usePdfEditorState } from "./hooks/usePdfEditorState";

const PDFFormEditor = ({
  formData,
  onClose,
  onSave,
  onNotify,
  isOpen = false,
  loading,
}) => {
  const [objectInspectorOpen, setObjectInspectorOpen] = React.useState(false);
  const [blockPanelOpen, setBlockPanelOpen] = React.useState(true);
  const [editableTitle, setEditableTitle] = React.useState("");

  const [openPreviewDialog, setOpenPreviewDialog] = React.useState(false);

  const {
    currentProcessingFile,
    setCurrentProcessingFile,
    numPages,
    pageNumber,
    scale,
    pdfInput,
    base64File,
    resizing,
    setResizing,
    moving,
    setMoving,
    isDrawing,
    setIsDrawing,
    isSelecting,
    setIsSelecting,
    rect,
    setRect,
    selectionRect,
    setSelectionRect,
    freeSelectStartPoint,
    setFreeSelectStartPoint,
    startPoint,
    setStartPoint,
    initialMousePosition,
    setInitialMousePosition,
    initialInputSize,
    setInitialInputSize,
    inputPosition,
    setInputPosition,
    currCursor,
    setCurrCursor,
    resizeDirection,
    setResizeDirection,
    selectToolActive,
    typingToolActive,
    focusedField,
    setFocusedField,
    highlightFields,
    setHighlightFields,
    textOverlayDisplayMode,
    setTextOverlayDisplayMode,
    unassignedFieldNames,
    pdfContainerRef,
    pdfDocumentRef,
    inputRef,
    childInputRef,
    fixedInputs,
    handleFieldChange,
    onDocumentLoadSuccess,
    onPageLoadSuccess,
    handleScaleChange,
    handlePageChange,
    handleSave,
    handleClose,
    activeEditorTool,
    handleEditorToolSelection,
    pdfScrollContainerRef,
    pushHistory,
    undo,
    redo,
    canRedo,
    canUndo,
    textSelectionMode,
    setTextSelectionMode,
  } = usePdfEditorState({ formData, isOpen, onClose, onSave, onNotify });

  React.useEffect(() => {
    setEditableTitle(
      formData?.guessedFormDetails?.label_guess ||
        formData?.formName ||
        formData?.fileName ||
        "Untitled Form",
    );
  }, [formData]);

  const handleObjectInspectorFieldChange = React.useCallback(
    (fieldKey, value) => {
      pushHistory?.();
      setCurrentProcessingFile((prev) => {
        const selectedTextItem =
          prev?.document?.tempFields?.selectedPdfTextItem || null;
        if (selectedTextItem) {
          const existingEdits = prev.document.tempFields.pdfTextEdits || [];
          const existingEdit = existingEdits.find(
            (edit) => edit.target_item_id === selectedTextItem.id,
          );

          let nextEdit = existingEdit
            ? { ...existingEdit }
            : {
                edit_id: `text_edit_${Date.now()}`,
                target_item_id: selectedTextItem.id,
                page: selectedTextItem.page,
                original_text: selectedTextItem.text,
                original_rect: { ...selectedTextItem.rect },
                rect: { ...selectedTextItem.rect },
                action: "replace",
                new_text: selectedTextItem.text,
              };

          if (fieldKey === "text") {
            nextEdit.new_text = value;
            nextEdit.action = value === "" ? "delete" : "replace";
          } else if (fieldKey === "action") {
            nextEdit.action = value;
          } else if (["x", "y", "width", "height"].includes(fieldKey)) {
            const rectKeyMap = { x: "X", y: "Y", width: "W", height: "H" };
            nextEdit.rect = {
              ...(nextEdit.rect || { ...selectedTextItem.rect }),
              [rectKeyMap[fieldKey]]: Number(value),
            };
            if (nextEdit.action !== "delete") {
              nextEdit.action = nextEdit.new_text ? "move" : "replace";
            }
          }

          const nextEdits = existingEdit
            ? existingEdits.map((edit) =>
                edit.target_item_id === selectedTextItem.id ? nextEdit : edit,
              )
            : [...existingEdits, nextEdit];

          return {
            ...prev,
            document: {
              ...prev.document,
              tempFields: {
                ...prev.document.tempFields,
                pdfTextEdits: nextEdits,
              },
            },
          };
        }

        if (!prev?.document?.tempFields?.selectedFormField) return prev;

        const selectedField = prev.document.tempFields.selectedFormField;
        const selectedFieldName = selectedField?.name;
        const selectedFieldId = selectedField?.id;

        if (fieldKey === "name") {
          const normalizedValue = String(value || "").trim();
          const duplicateField = (
            prev.document.tempFields.formFields || []
          ).find((field) => {
            const isSameField = selectedFieldId
              ? field?.id === selectedFieldId
              : field?.name === selectedFieldName;

            return (
              !isSameField && String(field?.name || "") === normalizedValue
            );
          });

          if (duplicateField) {
            onNotify?.(
              "error",
              "Field name already exists. Names must be unique across all pages.",
            );
            return prev;
          }

          value = normalizedValue;
        }

        const updatedFormFields = (
          prev.document.tempFields.formFields || []
        ).map((field) => {
          const isTargetField = selectedFieldId
            ? field?.id === selectedFieldId
            : field?.name === selectedFieldName;

          if (!isTargetField) return field;

          return {
            ...field,
            ...(field.type === "PDFTextField" ? { autoFontSize: true } : {}),
            [fieldKey]: value,
          };
        });

        const nextSelectedField = {
          ...selectedField,
          ...(selectedField?.type === "PDFTextField"
            ? { autoFontSize: true }
            : {}),
          [fieldKey]: value,
        };

        return {
          ...prev,
          document: {
            ...prev.document,
            tempFields: {
              ...prev.document.tempFields,
              formFields: updatedFormFields,
              selectedFormField: nextSelectedField,
            },
          },
        };
      });
    },
    [onNotify, setCurrentProcessingFile, pushHistory],
  );

  if (!isOpen || !formData) return null;

  return (
    <DndProvider backend={HTML5Backend}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
        }}
        tabIndex={0}
      >
        <PDFEditorHeader
          title={editableTitle}
          onTitleChange={setEditableTitle}
          onSave={handleSave}
          onClose={handleClose}
          loading={loading}
          setOpenPreviewDialog={setOpenPreviewDialog}
        />

        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#f5f5f5",
              overflow: "hidden",
            }}
          >
            <PDFEditorControlsBar
              activeTool={activeEditorTool}
              onToolSelect={handleEditorToolSelection}
              scale={scale}
              handleScaleChange={handleScaleChange}
              highlightFields={highlightFields}
              setHighlightFields={setHighlightFields}
              textOverlayDisplayMode={textOverlayDisplayMode}
              setTextOverlayDisplayMode={setTextOverlayDisplayMode}
              numPages={numPages}
              pageNumber={pageNumber}
              handlePageChange={handlePageChange}
              objectInspectorOpen={objectInspectorOpen}
              onToggleObjectInspector={() =>
                setObjectInspectorOpen((prev) => !prev)
              }
              blockPanelOpen={blockPanelOpen}
              onToggleBlockPanel={() => setBlockPanelOpen((prev) => !prev)}
              undoRedoStats={{ undo, redo, canRedo, canUndo }}
              textSelectionMode={textSelectionMode}
              setTextSelectionMode={setTextSelectionMode}
              loading={loading}
            />

            <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
              <PDFEditorLeftPanel
                isOpen={blockPanelOpen}
                currentProcessingFile={currentProcessingFile}
                setCurrentProcessingFile={setCurrentProcessingFile}
                onNotify={onNotify}
                loading={loading}
              />
              <PDFCanvas
                base64File={base64File}
                pdfInput={pdfInput}
                pdfScrollContainerRef={pdfScrollContainerRef}
                pdfContainerRef={pdfContainerRef}
                pdfDocumentRef={pdfDocumentRef}
                currentProcessingFile={currentProcessingFile}
                setCurrentProcessingFile={setCurrentProcessingFile}
                pageNumber={pageNumber}
                scale={scale}
                selectToolActive={selectToolActive}
                typingToolActive={typingToolActive}
                highlightFields={highlightFields}
                textOverlayDisplayMode={textOverlayDisplayMode}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                currCursor={currCursor}
                unassignedFieldNames={unassignedFieldNames}
                fixedInputs={fixedInputs}
                inputRef={inputRef}
                childInputRef={childInputRef}
                resizing={resizing}
                setResizing={setResizing}
                moving={moving}
                setMoving={setMoving}
                isDrawing={isDrawing}
                setIsDrawing={setIsDrawing}
                isSelecting={isSelecting}
                setIsSelecting={setIsSelecting}
                rect={rect}
                setRect={setRect}
                selectionRect={selectionRect}
                setSelectionRect={setSelectionRect}
                freeSelectStartPoint={freeSelectStartPoint}
                setFreeSelectStartPoint={setFreeSelectStartPoint}
                startPoint={startPoint}
                setStartPoint={setStartPoint}
                initialMousePosition={initialMousePosition}
                setInitialMousePosition={setInitialMousePosition}
                initialInputSize={initialInputSize}
                setInitialInputSize={setInitialInputSize}
                inputPosition={inputPosition}
                setInputPosition={setInputPosition}
                resizeDirection={resizeDirection}
                setResizeDirection={setResizeDirection}
                setCurrCursor={setCurrCursor}
                onDocumentLoadSuccess={onDocumentLoadSuccess}
                onPageLoadSuccess={onPageLoadSuccess}
                handleFieldChange={handleFieldChange}
                undoRedoStats={{ undo, redo, pushHistory }}
                activeEditorTool={activeEditorTool}
                textSelectionMode={textSelectionMode}
              />

              <ObjectInspectorDrawer
                isOpen={objectInspectorOpen}
                selectedField={
                  currentProcessingFile?.document?.tempFields?.selectedFormField
                }
                selectedTextItem={
                  currentProcessingFile?.document?.tempFields
                    ?.selectedPdfTextItem
                }
                pdfTextEdits={
                  currentProcessingFile?.document?.tempFields?.pdfTextEdits ||
                  []
                }
                currentDocument={
                  currentProcessingFile?.document?.tempFields?.currentDocument
                }
                onFieldChange={handleObjectInspectorFieldChange}
                loading={loading}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {openPreviewDialog && (
        <PreviewFormDialog
          open={openPreviewDialog}
          setOpen={setOpenPreviewDialog}
          base64File={base64File}
          pdfInput={pdfInput}
          currentProcessingFile={currentProcessingFile}
          setCurrentProcessingFile={setCurrentProcessingFile}
          pageNumber={pageNumber}
          onDocumentLoadSuccess={onDocumentLoadSuccess}
          onPageLoadSuccess={onPageLoadSuccess}
        />
      )}
    </DndProvider>
  );
};

export default PDFFormEditor;
